import React, { useState, useContext } from "react";
import { Table, Button } from "react-bootstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { getApiChara } from "../../../util/getApi";
import { getAuthToken } from "../../../util/auth";
import { useNavigate } from "react-router-dom";
import { FaPlay, FaEraser, FaEye, FaFileExport, FaPrint, FaTimes } from "react-icons/fa";
import classes from "./NewBill.module.css";
import AuthContext from "../../../store/auth-context";
import { getReportHeader, getReportStyles } from "../../../util/reportPrintHelper";
import { drawReportHeader, drawAuthorizedSignatory } from "../../../util/headerUtil";
import { exportTableToExcel } from "../../../util/excelExportUtil";
import { Spinner } from "react-bootstrap";
import { themeColors } from "../../../util/themeColors";
import { useTranslation } from "react-i18next";

const hexToRgb = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
};

const DatewiseBilling = () => {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [selectedPageSize, setSelectedPageSize] = useState("a4");
  const [selectedOrientation, setSelectedOrientation] = useState("portrait");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [exportFormat, setExportFormat] = useState("excel");
  const [isExporting, setIsExporting] = useState(false);

  const navigate = useNavigate();

  const totalBill = data.reduce((sum, row) => sum + (Number(row.totalAmount) || 0), 0);
  const totalReceived = data.reduce((sum, row) => sum + (Number(row.amountReceived) || 0), 0);
  const totalOutstanding = data.reduce((sum, row) => sum + (Number(row.outstandingAmount) || 0), 0);

  /* ------------------ Fetch Data ------------------ */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }

    if (!startDate || !endDate) {
      setError(t("Please select both dates."));
      setData([]);
      setHasSubmitted(false);
      return;
    }

    setHasSubmitted(true);
    setLoading(true);
    setError("");
    setData([]);

    try {
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/report/getDatewiseBillingListReportByDate?startDate=${startDate}&endDate=${endDate}`;

      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });

      if (!response.ok) throw new Error("Failed to fetch report");

      const result = await response.json();
      setData(Array.isArray(result) ? result : []);
    } catch {
      setError(t("Failed to fetch billing data"));
      setHasSubmitted(false);
    } finally {
      setLoading(false);
    }
  };

  const onClearFields = () => {
    setStartDate(today);
    setEndDate(today);
    setData([]);
    setHasSubmitted(false);
    setError("");
  };

  /* ------------------ PDF Generator ------------------ */
  const generatePDF = () => {
    const doc = new jsPDF(selectedOrientation, "pt", selectedPageSize);
    const pageWidth = doc.internal.pageSize.getWidth();

    const hospitalData = authCtx.hospitalData;
    const startY = drawReportHeader(doc, hospitalData, "DATEWISE BILLING REPORT", { hideSeparator: true, isExport: true, margin: 0, top: 0 });

    // Reset Text Color
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`From: ${startDate}   To: ${endDate}`, 40, startY);

    const themeName = authCtx.hospitalData?.applicationThemeColour || 'TEAL';
    const theme = themeColors[themeName] || themeColors.TEAL;

    doc.autoTable({
      head: [["Billing Date", "Total Amount", "Amount Received", "Outstanding Amount"]],
      body: data.map(row => [
        row.billingDate,
        row.totalAmount,
        row.amountReceived,
        row.outstandingAmount
      ]),
      foot: [["Total", totalBill.toFixed(2), totalReceived.toFixed(2), totalOutstanding.toFixed(2)]],
      startY: startY + 15,
      theme: 'grid',
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontSize: 10,
        halign: 'center',
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      },
      bodyStyles: { fontSize: 9 },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: 0,
        fontSize: 10,
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      },
      styles: {
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      margin: { left: 40, right: 40 },
      showFoot: 'lastPage'
    });

    drawAuthorizedSignatory(doc, hospitalData);
    return doc;
  };

  const onExportHandler = () => {
    if (exportFormat === "excel") {
      setIsExporting(true);
      setTimeout(() => {
        exportTableToExcel("datewiseBillingTable", "Datewise_Billing_Report");
        setIsExporting(false);
      }, 500);
    } else {
      generatePDF().save("datewise-billing-report.pdf");
    }
  };

  const onPreviewHandler = () => {
    const blob = generatePDF().output("blob");
    setPreviewUrl(URL.createObjectURL(blob));
  };

  const onPrintHandler = () => {
    if (!data.length) {
      alert(t("No data to print"));
      return;
    }

    // Create a hidden iframe for printing
    let printFrame = document.getElementById("printFrame");
    if (!printFrame) {
      printFrame = document.createElement("iframe");
      printFrame.id = "printFrame";
      printFrame.style.display = "none";
      document.body.appendChild(printFrame);
    }

    const tableToPrint = document.querySelector("#datewiseBillingTable");
    const tableClone = tableToPrint.cloneNode(true);
    // Remove Action Column from Clone
    const actionHeader = tableClone.querySelector("th:last-child");
    if (actionHeader && actionHeader.innerText.toLowerCase().includes("action")) {
      actionHeader.remove();
      tableClone.querySelectorAll("tr").forEach(tr => {
        const lastCell = tr.querySelector("td:last-child");
        if (lastCell) lastCell.remove();
      });
    }

    const printContent = `
      <html>
        <head>
          <title>Datewise Billing Report</title>
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
          <style>
            ${getReportStyles(selectedPageSize, selectedOrientation)}
          </style>
        </head>
        <body>
          ${getReportHeader(authCtx.hospitalData, t("Datewise Billing Report"))}
          <div class="meta-info">
             <div><strong>From:</strong> ${startDate}</div>
             <div><strong>To:</strong> ${endDate}</div>
          </div>
          ${tableClone.outerHTML}
        </body>
      </html>
    `;

    const frameDoc = printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(printContent);
    frameDoc.close();

    printFrame.contentWindow.focus();
    setTimeout(() => {
      printFrame.contentWindow.print();
    }, 500);
  };

  return (
    <div className="container-fluid">
      <div className="bg-light p-3 rounded shadow-sm border mb-4 d-flex justify-content-between align-items-center mt-3">
        <h5 className="textReports mb-0">{t("Datewise Billing Report")}</h5>
      </div>
      {/* Filters */}
      <form onSubmit={handleSubmit}>
        <div className="row g-2 align-items-end mb-4 bg-light p-3 rounded shadow-sm border mx-0 mt-3">
          <div className="col-md-3 col-6">
            <label className="fw-bold small mb-1">{t("Start Date")}</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ border: "1px solid #ced4da" }}
            />
          </div>

          <div className="col-md-3 col-6">
            <label className="fw-bold small mb-1">{t("End Date")}</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ border: "1px solid #ced4da" }}
            />
          </div>

          <div className="col-md-4 col-12 d-flex gap-2 align-items-end mt-2 mt-md-0">
            <button type="submit" className="btn-premium flex-grow-1">
              <FaPlay className="me-2" /> {t("SUBMIT")}
            </button>
            <button type="button" className="btn-secondary-premium flex-grow-1" onClick={onClearFields}>
              <FaEraser className="me-2" /> {t("CLEAR")}
            </button>
          </div>
        </div>
      </form>

      {/* Error */}
      {error && <p className="text-danger">{error}</p>}

      {/* Table (ONLY if no error) */}
      {hasSubmitted && !error && (
        <div className="printData table-responsive">
          <Table bordered striped hover id="datewiseBillingTable">
            <thead className="table-secondary">
              <tr>
                <th>{t("Billing Date")}</th>
                <th>{t("Total Amount")}</th>
                <th>{t("Amount Received")}</th>
                <th>{t("Outstanding Amount")}</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="text-center">
                    {t("Loading...")}
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center">
                    {t("No Record found")}
                  </td>
                </tr>
              ) : (
                data.map((row, index) => (
                  <tr key={index}>
                    <td>{row.billingDate}</td>
                    <td>{row.totalAmount}</td>
                    <td>{row.amountReceived}</td>
                    <td>{row.outstandingAmount}</td>
                  </tr>
                ))
              )}
            </tbody>
            {data.length > 0 && (
              <tfoot className="table-secondary font-weight-bold">
                <tr>
                  <td className="text-end"><strong>{t("Total")}:</strong></td>
                  <td><strong>{totalBill}</strong></td>
                  <td><strong>{totalReceived}</strong></td>
                  <td><strong>{totalOutstanding}</strong></td>
                </tr>
              </tfoot>
            )}
          </Table>
        </div>
      )}

      {/* Options */}
      {data.length > 0 && (
        <div className="options mt-4 bg-light p-3 rounded shadow-sm border mx-0">
          <div className="row g-3 justify-content-center align-items-end">
            <div className="col-md-auto col-6">
              <label className="fw-bold small d-block">{t("Select Size")}</label>
              <select
                className="form-select themed-select"
                value={selectedPageSize}
                onChange={(e) => setSelectedPageSize(e.target.value)}
              >
                <option value="a2">A2</option>
                <option value="a3">A3</option>
                <option value="a4">A4</option>
                <option value="a5">A5</option>
              </select>
            </div>

            <div className="col-md-auto col-6">
              <label className="fw-bold small d-block">{t("Orientation")}</label>
              <select
                className="form-select themed-select"
                value={selectedOrientation}
                onChange={(e) => setSelectedOrientation(e.target.value)}
              >
                <option value="portrait">{t("Portrait")}</option>
                <option value="landscape">{t("Landscape")}</option>
              </select>
            </div>

            <div className="col-md-auto col-6">
              <label className="fw-bold small d-block mb-1">&nbsp;</label>
              <button onClick={onPrintHandler} className="btn-premium w-100">
                <FaPrint className="me-2" /> {t("PRINT")}
              </button>
            </div>

            <div className="col-md-auto col-6">
              <label className="fw-bold small d-block mb-1">&nbsp;</label>
              <button onClick={onPreviewHandler} className="btn-premium w-100">
                <FaEye className="me-2" /> {t("PREVIEW")}
              </button>
            </div>

            <div className="col-md-auto col-12 mt-3 mt-md-0">
              <div className="d-flex gap-2">
                <div className="flex-grow-1">
                  <label className="fw-bold small d-block">{t("Format")}</label>
                  <select
                    className="form-select themed-select"
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value)}
                  >
                    <option value="pdf">{t("PDF Format")}</option>
                    <option value="excel">{t("Excel Format")}</option>
                  </select>
                </div>
                <div className="align-self-end">
                  <button
                    onClick={onExportHandler}
                    className="btn-export-premium d-flex align-items-center"
                    disabled={isExporting}
                  >
                    {isExporting ? <Spinner animation="border" size="sm" className="me-2" /> : <FaFileExport className="me-2" />}
                    {isExporting ? t("EXPORTING...") : t("EXPORT")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewUrl && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header" style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}>
                <h5 className="modal-title d-flex align-items-center" style={{ color: 'white' }}>
                  <FaEye className="me-2" /> {t("REPORT PREVIEW")}
                </h5>
                <button
                  type="button"
                  className="btn btn-link text-white p-0 border-0"
                  onClick={() => setPreviewUrl(null)}
                  aria-label="Close"
                  style={{ fontSize: "1.5rem", lineHeight: "1" }}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="modal-body p-0 bg-dark">
                <iframe
                  title="PDF Preview"
                  src={previewUrl}
                  width="100%"
                  height="750"
                  frameBorder="0"
                  className="w-100"
                ></iframe>
              </div>
              <div className="modal-footer bg-light p-2">
                <Button className="btn-secondary-premium" onClick={() => setPreviewUrl(null)}>
                  {t("CLOSE PREVIEW")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatewiseBilling;
